/**
 * Canonical field/billing/SLA timezone for the FRONTEND (§15.4) — mirrors the
 * backend `field_zone_for_ticket`. Every surface that renders a ticket's
 * field/visit/SLA time resolves the zone through here so the same UTC instant
 * never shows as 10:00 CEST in one place and 10:30 UTC in another.
 *
 * NOTE: this is a plain function, not a React hook (the spec called it
 * `useFieldZone`). The backend already resolves the on-site zone via the
 * canonical chain (site → country → tenant → UTC) and ships it on the payload
 * as `site_timezone`, so there's nothing to fetch — a pure helper is correct
 * and, unlike a `use*` hook, is safe to call inside list/card render loops.
 *
 * Built on the platform `Intl` API; reuses `zoneAbbrev` from ./tz for labels.
 */
import { zoneAbbrev } from './tz';
function toDate(instant) {
    if (!instant)
        return null;
    const d = instant instanceof Date ? instant : new Date(instant);
    return Number.isNaN(d.getTime()) ? null : d;
}
function zoneParts(d, tz) {
    const opts = {
        weekday: 'short',
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        hourCycle: 'h23',
    };
    let useTz = tz;
    let parts;
    try {
        parts = new Intl.DateTimeFormat('en-GB', { timeZone: useTz, ...opts }).formatToParts(d);
    }
    catch {
        console.warn(`[fieldZone] invalid timezone "${tz}" — falling back to UTC`);
        useTz = 'UTC';
        parts = new Intl.DateTimeFormat('en-GB', { timeZone: useTz, ...opts }).formatToParts(d);
    }
    const p = {};
    for (const part of parts)
        if (part.type !== 'literal')
            p[part.type] = part.value;
    return {
        tz: useTz,
        weekday: p.weekday ?? '',
        day: p.day ?? '',
        month: p.month ?? '',
        year: p.year ?? '',
        hour: p.hour ?? '',
        minute: p.minute ?? '',
    };
}
/** Full field-time string, matching the backend: "Mon 15 Jun 2026, 10:00 CEST". */
export function formatFieldTime(instant, tz) {
    const d = toDate(instant);
    if (!d)
        return '—';
    const q = zoneParts(d, tz);
    return `${q.weekday} ${q.day} ${q.month} ${q.year}, ${q.hour}:${q.minute} ${zoneAbbrev(q.tz, d.toISOString())}`;
}
/** Compact field-time for cards: "15 Jun 10:00 CEST" (no weekday/year). */
export function formatFieldTimeShort(instant, tz) {
    const d = toDate(instant);
    if (!d)
        return '—';
    const q = zoneParts(d, tz);
    return `${q.day} ${q.month} ${q.hour}:${q.minute} ${zoneAbbrev(q.tz, d.toISOString())}`;
}
/** Time-only in the zone: "10:00" (no date, no label) — for big-number views. */
export function formatFieldClock(instant, tz) {
    const d = toDate(instant);
    if (!d)
        return '—';
    const q = zoneParts(d, tz);
    return `${q.hour}:${q.minute}`;
}
/**
 * Build the field-zone helper from a payload's resolved `site_timezone`
 * (already computed by the backend canonical chain). Falls back to "UTC" — the
 * same terminal fallback as the backend — when none is present.
 */
export function fieldZone(siteTimezone) {
    const tz = siteTimezone && siteTimezone.trim() ? siteTimezone.trim() : 'UTC';
    return {
        tz,
        format: (instant) => formatFieldTime(instant, tz),
        formatShort: (instant) => formatFieldTimeShort(instant, tz),
        clock: (instant) => formatFieldClock(instant, tz),
        abbrev: (instant) => {
            const d = toDate(instant);
            return zoneAbbrev(tz, d ? d.toISOString() : null);
        },
    };
}
